import { useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, useCollectionBinding, usePropertyAddressUrl } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import {
  MultihubLogoMonoWhite,
  repliWhiteLogoUrl,
  FacebookIcon,
  InstagramIcon,
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
} from '@vibesites/assets';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBanSmoking } from '@fortawesome/free-solid-svg-icons/faBanSmoking';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useColor4 } from '../hooks/useColor4';
import { usePropertyCountry } from '../utils/propertyCountry';

// ============================================================================
// RentCafe / Yardi chat launcher stacking
// ============================================================================
// Vendor injects the launcher into <body> with an inline z-index that sits
// under Avenue's open header. Keep !important so that inline style loses.
// Sit just above the open header (120). Only the real launcher nodes —
// never broad yardi/rentcafe iframe or text matches.
// ============================================================================

const CHAT_LAUNCHER_STYLE_ID = 'avenue-chat-launcher-z';
/** Just above Avenue's open header stacking context. */
const CHAT_LAUNCHER_Z = '120';
const CHAT_LAUNCHER_SELECTOR =
  '.rcc-app-cta-wrap, .rcc-app-controls, #conversations-webchat-toggle';

function isChatWrap(el: Element): boolean {
  return el instanceof HTMLElement && el.classList.contains('rcc-app-cta-wrap');
}

function isChatLauncher(el: Element): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return (
    el.classList.contains('rcc-app-cta-wrap') ||
    el.classList.contains('rcc-app-controls') ||
    el.id === 'conversations-webchat-toggle'
  );
}

/** Furthest fixed ancestor toward <body>, or null. */
function getOutermostFixedAncestor(el: HTMLElement): HTMLElement | null {
  let outermost: HTMLElement | null = null;
  let node: HTMLElement | null = el;
  while (node && node !== document.documentElement) {
    if (getComputedStyle(node).position === 'fixed') {
      outermost = node;
    }
    node = node.parentElement;
  }
  return outermost;
}

function pinChatLauncherZ(el: HTMLElement): void {
  el.style.setProperty('z-index', CHAT_LAUNCHER_Z, 'important');
}

/**
 * Pin only the launcher itself, and its outermost fixed ancestor only when
 * that ancestor is the chat wrap (.rcc-app-cta-wrap) — never a random fixed
 * page shell that happens to contain the widget.
 */
function pinLauncherStack(el: HTMLElement): void {
  pinChatLauncherZ(el);
  const outermost = getOutermostFixedAncestor(el);
  if (outermost && outermost !== el && isChatWrap(outermost)) {
    pinChatLauncherZ(outermost);
  }
}

function pinAllChatLaunchers(root: ParentNode = document): void {
  root.querySelectorAll(CHAT_LAUNCHER_SELECTOR).forEach((node) => {
    if (node instanceof HTMLElement) pinLauncherStack(node);
  });
}

function useChatLauncherZIndex(): void {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    let style = document.getElementById(CHAT_LAUNCHER_STYLE_ID) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement('style');
      style.id = CHAT_LAUNCHER_STYLE_ID;
      style.textContent = `
${CHAT_LAUNCHER_SELECTOR} {
  z-index: ${CHAT_LAUNCHER_Z} !important;
}
`;
      document.head.appendChild(style);
    }

    pinAllChatLaunchers();

    const observer = new MutationObserver((mutations) => {
      let shouldScan = false;
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.target instanceof Element) {
          if (isChatLauncher(mutation.target)) {
            pinLauncherStack(mutation.target as HTMLElement);
          }
          continue;
        }
        if (mutation.type !== 'childList') continue;
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (isChatLauncher(node)) {
            pinLauncherStack(node as HTMLElement);
            shouldScan = true;
          } else if (node.querySelector?.(CHAT_LAUNCHER_SELECTOR)) {
            shouldScan = true;
          }
        }
      }
      if (shouldScan) pinAllChatLaunchers();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'id'],
    });

    return () => {
      observer.disconnect();
    };
  }, []);
}

// ============================================================================
// Types
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

/** Formats a phone string into (XXX) XXX-XXXX. Passes through if already formatted or unrecognizable. */
function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  // Strip leading country code "1" if 11 digits
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  // Already has am/pm — normalize spacing/casing ("9:00AM" → "9:00 am")
  const ampm = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*([ap])\.?m\.?$/i);
  if (ampm) {
    let hours = parseInt(ampm[1], 10);
    const minutes = ampm[2];
    const period = ampm[3].toLowerCase() === 'a' ? 'am' : 'pm';
    if (hours === 0) hours = 12;
    else if (hours > 12) hours -= 12;
    return `${hours}:${minutes} ${period}`;
  }
  if (/[ap]\.?m\.?/i.test(trimmed)) {
    return trimmed
      .replace(/\s+/g, ' ')
      .replace(/\s*\.?\s*([ap])\.?m\.?/i, (_, p) => ` ${p.toLowerCase()}m`)
      .trim();
  }
  // Parse 24h "HH:MM", "H:MM", or with seconds
  const match = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};

interface GroupedHours {
  label: string;
  hours: string;
}

function normalizeDay(day: string): string {
  const cleaned = day.trim().toLowerCase();
  // Prefer canonical keys so "Thursday" / "Thu" / "Thurs" all map to "thu"
  const aliases: Record<string, string> = {
    m: 'mon', mon: 'mon', monday: 'mon',
    t: 'tue', tu: 'tue', tue: 'tue', tues: 'tue', tuesday: 'tue',
    w: 'wed', wed: 'wed', wednesday: 'wed',
    r: 'thu', th: 'thu', thu: 'thu', thur: 'thu', thurs: 'thu', thursday: 'thu',
    f: 'fri', fri: 'fri', friday: 'fri',
    s: 'sat', sa: 'sat', sat: 'sat', saturday: 'sat',
    u: 'sun', su: 'sun', sun: 'sun', sunday: 'sun',
  };
  if (aliases[cleaned]) return aliases[cleaned];
  return cleaned.slice(0, 3);
}

function hoursKey(entry: OfficeHour): string {
  const openClosed =
    (hasValue(entry.open_time) && entry.open_time.trim().toLowerCase() === 'closed') ||
    (hasValue(entry.close_time) && entry.close_time.trim().toLowerCase() === 'closed');
  if (openClosed || (!hasValue(entry.open_time) && !hasValue(entry.close_time))) {
    return 'closed';
  }
  return `${formatTime(entry.open_time || '')}|${formatTime(entry.close_time || '')}`;
}

function formatHoursDisplay(key: string): string {
  if (key === 'closed') return 'Closed';
  const [open, close] = key.split('|');
  return `${open} - ${close}`;
}

/**
 * Index by weekday (last entry wins), fill omitted days as Closed, then merge
 * consecutive days that share the same hours into ranges like "Monday - Friday".
 */
function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  const byDay = new Map<string, OfficeHour>();
  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (DAY_ORDER.includes(key)) byDay.set(key, entry);
  }

  const groups: GroupedHours[] = [];
  for (const dayKey of DAY_ORDER) {
    const entry = byDay.get(dayKey) ?? { day: DAY_LABELS[dayKey], open_time: null, close_time: null };
    const key = hoursKey(entry);
    const hours = formatHoursDisplay(key);
    const dayLabel = DAY_LABELS[dayKey];

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hours) {
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} - ${dayLabel}`;
    } else {
      groups.push({ label: dayLabel, hours });
    }
  }

  return groups;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_black_logo', suggestedFields: ['property_mono_black_logo', 'property_primary_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const addressGroupDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address Text',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '',
    },
    url: {
      label: 'Map URL',
      type: 'url',
      // URL has no defaultField — defaults to usePropertyAddressUrl() at render
      // time. Editor overrides still win.
      defaultValue: '',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_company_mono_white_logo', suggestedFields: ['property_company_mono_white_logo', 'property_company_logo', 'property_company_mono_black_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_facebook_url', suggestedFields: ['property_facebook_url'] },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_instagram_url', suggestedFields: ['property_instagram_url'] },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const poweredByLogoDef: SlotGroupDefinition = {
  label: 'Powered By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multihub' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.multihub.io/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};


// ============================================================================
// Component
// ============================================================================

export function Footer() {
  useChatLauncherZIndex();

  const phone = useContentToken('footer.phone');
  const callUsLabel = useContentToken('footer.call_us_label');
  const visitUsLabel = useContentToken('footer.visit_us_label');
  const officeHoursLabel = useContentToken('footer.office_hours_label');
  const managedByText = useContentToken('footer.managed_by_text');
  const poweredByText = useContentToken('footer.powered_by_text');
  const designByText = useContentToken('footer.design_by_text');
  const copyrightText = useContentToken('footer.copyright');

  const propertyAddressUrl = usePropertyAddressUrl();
  // Equal Housing Opportunity is a US fair-housing mark.
  const showFairHousing = usePropertyCountry() === 'us';

  const address = useSlotGroup('footer.address', addressGroupDef);
  // Editor URL → hook fallback → '#'. Editor overrides still win.
  const addressHref = address.url || propertyAddressUrl || '#';

  const logo = useSlotGroup('footer.logo', logoSlotDef);
  const managedByLogo = useSlotGroup('footer.managed_by_logo', managedByLogoDef);
  const poweredByLogo = useSlotGroup('footer.powered_by_logo', poweredByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const facebookLink = useSlotGroup('footer.facebook_link', facebookLinkDef);
  const instagramLink = useSlotGroup('footer.instagram_link', instagramLinkDef);
  const color4 = useColor4();
  const socialLinkClassName = color4
    ? 'hover:opacity-70 transition-opacity'
    : 'text-gray-900 hover:text-gray-600 transition-colors';
  const socialLinkStyle = color4 ? { color: color4.color } : undefined;
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);

  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });

  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  return (
    <footer className="bg-gray-100 border-t border-gray-200 text-gray-900">
      {/* ---- Main Footer Content ---- */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16">
        {/* 3-col only at lg — tablet (~md) columns are too narrow for day + hours */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12">
          {/* Column 1: Logo + Social */}
          <div className="flex flex-col items-center lg:items-start">
            <EditableSlotGroup groupPath="footer.logo" definition={logoSlotDef}>
              <a href="/" className="block mb-6">
                {logo.image ? (
                  <VibesitesImage
                    url={logo.image}
                    alt={logo.alt || 'Community Logo'}
                    sizes="200px"
                    className="h-20 w-auto max-w-[200px] object-contain"
                  />
                ) : (
                  <div className="font-heading text-2xl text-gray-900">Our Community</div>
                )}
              </a>
            </EditableSlotGroup>

            <div className="flex items-center gap-4">
              {facebookLink.url && (
                <EditableSlotGroup groupPath="footer.facebook_link" definition={facebookLinkDef}>
                  <a href={facebookLink.url} target={facebookLink._target || '_blank'} rel="noopener noreferrer" aria-label="Facebook" className={socialLinkClassName} style={socialLinkStyle}>
                    <FacebookIcon size={20} />
                  </a>
                </EditableSlotGroup>
              )}
              {instagramLink.url && (
                <EditableSlotGroup groupPath="footer.instagram_link" definition={instagramLinkDef}>
                  <a href={instagramLink.url} target={instagramLink._target || '_blank'} rel="noopener noreferrer" aria-label="Instagram" className={socialLinkClassName} style={socialLinkStyle}>
                    <InstagramIcon size={20} />
                  </a>
                </EditableSlotGroup>
              )}
            </div>
          </div>

          {/* Column 2: Contact Info — text-center so full-width editable wrappers still center on mobile */}
          <div className="flex flex-col items-center lg:items-start gap-5 text-center lg:text-left w-full">
            {/* Phone */}
            <div className="w-full">
              <EditableToken path="footer.call_us_label" defaultValue="Call Us" as="p" className="font-body text-[11px] uppercase tracking-[0.15em] text-gray-900 mb-1">{callUsLabel}</EditableToken>
              <a href={`tel:${phone?.replace(/[^+\d]/g, '')}`} className="font-body text-sm text-gray-700 hover:text-gray-900 transition-colors">
                <EditableToken path="footer.phone" defaultField="property_call_tracking_phone" defaultValue="(555) 555-5555" as="span">
                  {formatPhone(phone)}
                </EditableToken>
              </a>
            </div>

            {/* Address */}
            <div className="w-full">
              <EditableToken path="footer.visit_us_label" defaultValue="Visit Us" as="p" className="font-body text-[11px] uppercase tracking-[0.15em] text-gray-900 mb-1">{visitUsLabel}</EditableToken>
              <EditableSlotGroup groupPath="footer.address" definition={addressGroupDef}>
                <a
                  href={addressHref}
                  target={address._target || '_blank'}
                  rel="noopener noreferrer"
                  className="font-body text-sm text-gray-700 hover:text-gray-900 transition-colors block"
                >
                  <span className="block">{address.text}</span>
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Column 3: Office Hours */}
          <div className="flex flex-col items-center lg:items-start">
            <EditableToken path="footer.office_hours_label" defaultValue="Office Hours" as="p" className="font-body text-[11px] uppercase tracking-[0.15em] text-gray-900 mb-3">{officeHoursLabel}</EditableToken>
            {groupedHours.length > 0 ? (
              <dl className="space-y-2 w-full max-w-[320px]">
                {groupedHours.map((group, index) => (
                  <div key={index} className="flex justify-between gap-4 font-body text-sm text-gray-700">
                    <dt className="min-w-0">{group.label}</dt>
                    <dd className="shrink-0 text-right whitespace-nowrap">{group.hours}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="font-body text-sm text-gray-400">Office hours will be displayed here.</p>
            )}
          </div>
        </div>
      </div>

      {/* ---- Compliance Icons + Legal Links + Copyright ---- */}
      <div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-6 text-gray-500">
              {showFairHousing && <FairHousingIcon size={32} color="currentColor" />}
              <PetsAllowedIcon size={32} color="currentColor" />
              <WheelchairIcon size={32} color="currentColor" />
              <FontAwesomeIcon
                icon={faBanSmoking}
                style={{ width: 32, height: 32 }}
                color="currentColor"
                aria-label="No Smoking"
                role="img"
              />
            </div>
            <nav className="flex items-center gap-2 font-body text-xs text-gray-500" aria-label="Legal links">
              <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
                <a href={accessibilityLink.url || '/accessibility'} target={accessibilityLink._target || '_self'} className="hover:text-gray-700 transition-colors">
                  {accessibilityLink.text || 'Accessibility'}
                </a>
              </EditableSlotGroup>
              <span aria-hidden="true">/</span>
              <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
                <a href={privacyPolicyLink.url || '/privacy-policy'} target={privacyPolicyLink._target || '_self'} className="hover:text-gray-700 transition-colors">
                  {privacyPolicyLink.text || 'Privacy Policy'}
                </a>
              </EditableSlotGroup>
              <span aria-hidden="true">/</span>
              <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
                <a href={sitemapLink.url || '/sitemap'} target={sitemapLink._target || '_self'} className="hover:text-gray-700 transition-colors">
                  {sitemapLink.text || 'Sitemap'}
                </a>
              </EditableSlotGroup>
            </nav>
            <EditableToken path="footer.copyright" defaultValue="© 2025 All Rights Reserved." as="p" className="font-body text-xs text-gray-400">
              {copyrightText}
            </EditableToken>
          </div>
        </div>
      </div>

      {/* ---- Bottom Attribution Bar ---- */}
      <div className="bg-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-5 pb-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Managed By — left aligned (prominent) */}
            <div className="flex flex-col items-center md:items-start gap-2">
              <EditableToken path="footer.managed_by_text" defaultValue="Professionally Managed by" as="span" className="font-body text-[11px] text-white">
                {managedByText}
              </EditableToken>
              <EditableSlotGroup groupPath="footer.managed_by_logo" definition={managedByLogoDef}>
                {managedByLogo.image ? (
                  <a href={managedByLogo.url || '#'} target={managedByLogo._target || '_blank'} rel="noopener noreferrer">
                    <VibesitesImage
                      url={managedByLogo.image}
                      alt={managedByLogo.alt || 'Property Management Company'}
                      sizes="200px"
                      className="h-9 w-auto max-w-[200px] object-contain opacity-90 hover:opacity-100 transition-opacity"
                    />
                  </a>
                ) : null}
              </EditableSlotGroup>
            </div>

            {/* Powered By — center aligned (smaller) */}
            <EditableSlotGroup groupPath="footer.powered_by_logo" definition={poweredByLogoDef}>
              <div className="flex flex-col items-center gap-1">
                <EditableToken path="footer.powered_by_text" defaultValue="Powered by" as="span" className="font-body text-[9px] text-white">
                  {poweredByText}
                </EditableToken>
                <a href={poweredByLogo.url || 'https://www.multihub.io/'} target={poweredByLogo._target || '_blank'} rel="noopener noreferrer" className="opacity-90 hover:opacity-100 transition-opacity">
                  <MultihubLogoMonoWhite height={27} alt={poweredByLogo.alt || 'Multihub'} />
                </a>
              </div>
            </EditableSlotGroup>

            {/* Design By — right aligned (smaller) */}
            <EditableSlotGroup groupPath="footer.design_by_logo" definition={designByLogoDef}>
              <div className="flex flex-col items-center md:items-end gap-1">
                <EditableToken path="footer.design_by_text" defaultValue="Website Design by" as="span" className="font-body text-[9px] text-white">
                  {designByText}
                </EditableToken>
                <a href={designByLogo.url || 'https://www.repli360.com/'} target={designByLogo._target || '_blank'} rel="noopener noreferrer" className="opacity-90 hover:opacity-100 transition-opacity">
                  <img src={repliWhiteLogoUrl} alt={designByLogo.alt || 'Repli'} height={36} className="h-9 w-auto" />
                </a>
              </div>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'footer.call_us_label': {
      label: 'Call Us Label',
      type: 'text',
      defaultValue: 'Call Us',
    },
    'footer.visit_us_label': {
      label: 'Visit Us Label',
      type: 'text',
      defaultValue: 'Visit Us',
    },
    'footer.office_hours_label': {
      label: 'Office Hours Label',
      type: 'text',
      defaultValue: 'Office Hours',
    },
    'footer.managed_by_text': {
      label: 'Managed By Text',
      type: 'text',
      defaultValue: 'Professionally Managed by',
    },
    'footer.powered_by_text': {
      label: 'Powered By Text',
      type: 'text',
      defaultValue: 'Powered by',
    },
    'footer.design_by_text': {
      label: 'Website Design By Text',
      type: 'text',
      defaultValue: 'Website Design by',
    },
    'footer.copyright': {
      label: 'Copyright',
      type: 'text',
      defaultValue: '© 2025 All Rights Reserved.',
    },
  },
  slotGroups: {
    'footer.address': addressGroupDef,
    'footer.logo': logoSlotDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.powered_by_logo': poweredByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.facebook_link': facebookLinkDef,
    'footer.instagram_link': instagramLinkDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as any).contentSchema = contentSchema;

export default Footer;
